import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaLock } from 'react-icons/fa';
import MyHeader from '../../components/common/MyHeader';
import AdminMyHeader from '../../components/common/admin/MyHeader';
import { HomeHeader } from '../Home/HomeHeader';
import { getUserProfile, updateUserProfile } from '../../firebase';
import { getStrengthColors } from '../../utils/strengthColors';

const Settings = () => {
    const { user, groupMembers, groupInfo, groupName, courseInfo, isAdmin } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const currentMember = groupMembers?.find(m => m.email === user?.email);
    const settingsHeaderVariant = location.state?.settingsHeaderVariant;
    const headerCourseName =
        location.state?.settingsCourseName ||
        groupInfo?.name ||
        courseInfo?.name ||
        '';
    const headerGroupName =
        location.state?.settingsGroupName ||
        groupName ||
        '';
    const showCourseHeader =
        location.state?.settingsHeaderMode === 'course' ||
        (
            location.state?.settingsHeaderMode == null &&
            groupInfo &&
            typeof groupInfo === 'object' &&
            groupInfo?.name &&
            groupName
        );
    const homeLink = isAdmin ? '/admin/home' : '/student/home';
    const connectionMode = showCourseHeader ? 'group' : 'home';
    const connectionNavState = {
        connectionMode,
        returnTo: 'settings',
        settingsHeaderMode: showCourseHeader ? 'course' : 'home',
        settingsHeaderVariant,
        settingsCourseName: headerCourseName,
        settingsGroupName: headerGroupName,
    };
    const renderHeader = () => {
        if (!showCourseHeader) {
            return <HomeHeader text="Settings" link={homeLink} />;
        }

        if (settingsHeaderVariant === 'admin-course' || (isAdmin && showCourseHeader)) {
            return (
                <AdminMyHeader
                    hideNav
                    hideControls
                    overrideCourseName={headerCourseName}
                    overrideGroupName={headerGroupName}
                />
            );
        }

        return <MyHeader hideNav />;
    };

    const [bio, setBio] = useState('');
    const [phone, setPhone] = useState('');
    const [slack, setSlack] = useState('');
    const [linkedin, setLinkedin] = useState('');
    const [strengthInput, setStrengthInput] = useState('');
    const [strengths, setStrengths] = useState([]);
    const [originalBio, setOriginalBio] = useState('');
    const [originalPhone, setOriginalPhone] = useState('');
    const [originalSlack, setOriginalSlack] = useState('');
    const [originalLinkedin, setOriginalLinkedin] = useState('');
    const [originalStrengths, setOriginalStrengths] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saveMessage, setSaveMessage] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [discordConnection, setDiscordConnection] = useState('');
    const [githubConnection, setGithubConnection] = useState('');

    useEffect(() => {
        const fetchUserProfile = async () => {
            if (user && user.email) {
                try {
                    const profile = await getUserProfile(user.email);

                    // Extract first name and last name from full name
                    if (profile && profile.name) {
                        const nameParts = profile.name.trim().split(' ');
                        setFirstName(nameParts[0] || '');
                        setLastName(nameParts.length > 1 ? nameParts[nameParts.length - 1] : '');
                    }

                    const currentBio = profile?.bio || '';
                    setBio(currentBio);
                    setOriginalBio(currentBio);

                    const currentPhone = profile?.phone || '';
                    setPhone(currentPhone);
                    setOriginalPhone(currentPhone);

                    const currentSlack = profile?.slack || '';
                    setSlack(currentSlack);
                    setOriginalSlack(currentSlack);

                    const currentLinkedin = profile?.linkedin || '';
                    setLinkedin(currentLinkedin);
                    setOriginalLinkedin(currentLinkedin);

                    setDiscordConnection(profile?.discord || '');
                    setGithubConnection(profile?.github || '');

                    const currentStrengths = profile?.strengths || [];
                    setStrengths(currentStrengths);
                    setOriginalStrengths([...currentStrengths]);
                } catch (error) {
                    console.error('Error fetching user profile:', error);
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchUserProfile();
    }, [user]);

    const handleSaveChanges = async () => {
        if (!user || !user.email) return;

        setSaving(true);
        setSaveMessage('');

        try {
            const profileData = {
                bio: bio,
                phone: phone,
                slack: slack,
                linkedin: linkedin,
                strengths: strengths
            };

            await updateUserProfile(user.email, profileData);

            setOriginalBio(bio);
            setOriginalPhone(phone);
            setOriginalSlack(slack);
            setOriginalLinkedin(linkedin);
            setOriginalStrengths([...strengths]);

            setSaveMessage('Changes saved successfully!');
            setTimeout(() => setSaveMessage(''), 3000);
        } catch (error) {
            console.error('Error saving changes:', error);
            setSaveMessage('Error saving changes. Please try again.');
            setTimeout(() => setSaveMessage(''), 3000);
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setBio(originalBio);
        setPhone(originalPhone);
        setSlack(originalSlack);
        setLinkedin(originalLinkedin);
        setStrengths([...originalStrengths]);
        setSaveMessage('');
    };

    const handleGoBack = () => {
        navigate(-1);
    };

    const addStrength = () => {
        if (strengthInput.trim() && !strengths.includes(strengthInput.trim())) {
            setStrengths([...strengths, strengthInput.trim()]);
            setStrengthInput('');
        }
    };

    const removeStrength = (strengthToRemove) => {
        setStrengths(strengths.filter(strength => strength !== strengthToRemove));
    };

    const clearPhone = () => {
        setPhone('');
    };

    const clearSlack = () => {
        setSlack('');
    };

    const clearLinkedin = () => {
        setLinkedin('');
    };

    const addPredefinedStrength = (strength) => {
        if (!strengths.includes(strength)) {
            setStrengths([...strengths, strength]);
        }
    };

    const predefinedStrengths = [
        'Leadership',
        'Communication',
        'Creativity',
        'Research',
        'UI/UX Design',
        'JavaScript',
        'Python',
        'C++',
        'Java',
        'SQL / Databases',
        'Cloud Server (AWS, Azure, GCP)'
    ];

    const handleStrengthInputKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addStrength();
        }
    };

    const hasChanges =
        bio !== originalBio ||
        phone !== originalPhone ||
        slack !== originalSlack ||
        linkedin !== originalLinkedin ||
        JSON.stringify(strengths) !== JSON.stringify(originalStrengths);

    if (loading) {
        return (
            <>
                {renderHeader()}
                <PageWrapper>
                    <Container>
                        <LoadingMessage>Loading...</LoadingMessage>
                    </Container>
                </PageWrapper>
            </>
        );
    }

    return (
        <>
            {renderHeader()}
            <PageWrapper>
                <Container>
                    <BackButtonWrapper>
                        <BackButton onClick={handleGoBack}>
                            <FaArrowLeft style={{ marginRight: '8px' }} />
                            <span>Back</span>
                        </BackButton>
                    </BackButtonWrapper>
                    <Header>
                        <Title>Settings</Title>
                        <Subtitle>Manage your profile and preferences</Subtitle>
                    </Header>
                    <Card>
                        <Section>
                                <SectionTitle>Personal Information</SectionTitle>
                                <PersonalInfoGrid>
                                    <InfoField>
                                        <Label>First</Label>
                                        <ReadOnlyInput value={firstName || ''} readOnly />
                                    </InfoField>
                                    <InfoField>
                                        <Label>Last</Label>
                                        <ReadOnlyInput value={lastName || ''} readOnly />
                                    </InfoField>
                                    <InfoField>
                                        <Label>Email</Label>
                                        <ReadOnlyInput value={user?.email || ''} readOnly />
                                    </InfoField>
                                    <InfoField>
                                        <Label>Phone</Label>
                                        <PhoneInputContainer>
                                            <Input
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value)}
                                                placeholder="(+1)xxx-xxx-xxxx"
                                            />
                                            {phone && (
                                                <PhoneDeleteButton onClick={clearPhone}>
                                                    ×
                                                </PhoneDeleteButton>
                                            )}
                                        </PhoneInputContainer>
                                    </InfoField>
                                    <BioFieldContainer>
                                        <Label>Bio</Label>
                                        <BioTextArea
                                            value={bio}
                                            onChange={(e) => setBio(e.target.value)}
                                            placeholder="Tell us about yourself..."
                                            rows={3}
                                        />
                                    </BioFieldContainer>
                                </PersonalInfoGrid>
                            </Section>

                            <Section>
                                <SectionTitle>Social & Contact</SectionTitle>
                                <SocialGrid>
                                    <SocialField>
                                        <SocialHeader>
                                            <SocialIconImage src="/images/Discord.png" alt="Discord" />
                                            <SocialLabel>Discord</SocialLabel>
                                            <ManageLink onClick={() => navigate('/discord-setup', { state: connectionNavState })}>
                                                Manage Connections
                                            </ManageLink>
                                        </SocialHeader>
                                        <ReadOnlyInputWrapper>
                                            <ReadOnlyInput
                                                value={showCourseHeader ? (currentMember?.discord_username || '') : discordConnection}
                                                readOnly
                                                placeholder="Set up in Discord Setup"
                                            />
                                            <LockIcon><FaLock /></LockIcon>
                                        </ReadOnlyInputWrapper>
                                    </SocialField>

                                    <SocialField>
                                        <SocialHeader>
                                            <SocialIconImage src="/images/github.svg" alt="GitHub" />
                                            <SocialLabel>GitHub</SocialLabel>
                                            <ManageLink onClick={() => navigate('/group-setup', { state: connectionNavState })}>
                                                Manage Connections
                                            </ManageLink>
                                        </SocialHeader>
                                        <ReadOnlyInputWrapper>
                                            <ReadOnlyInput
                                                value={showCourseHeader ? (currentMember?.github_username || '') : githubConnection}
                                                readOnly
                                                placeholder="Set up in GitHub Setup"
                                            />
                                            <LockIcon><FaLock /></LockIcon>
                                        </ReadOnlyInputWrapper>
                                    </SocialField>

                                    <SocialField>
                                        <SocialHeader>
                                            <SocialIconImage src="/images/Slack.png" alt="Slack" />
                                            <SocialLabel>Slack</SocialLabel>
                                        </SocialHeader>
                                        <SocialInputContainer>
                                            <Input
                                                value={slack}
                                                onChange={(e) => setSlack(e.target.value)}
                                                placeholder="Enter Slack username"
                                            />
                                            {slack && (
                                                <SocialDeleteButton onClick={clearSlack}>
                                                    ×
                                                </SocialDeleteButton>
                                            )}
                                        </SocialInputContainer>
                                    </SocialField>

                                    <SocialField>
                                        <SocialHeader>
                                            <SocialIconImage src="/images/LinkedIn.png" alt="LinkedIn" />
                                            <SocialLabel>LinkedIn</SocialLabel>
                                        </SocialHeader>
                                        <SocialInputContainer>
                                            <Input
                                                value={linkedin}
                                                onChange={(e) => setLinkedin(e.target.value)}
                                                placeholder="Enter LinkedIn profile URL"
                                            />
                                            {linkedin && (
                                                <SocialDeleteButton onClick={clearLinkedin}>
                                                    ×
                                                </SocialDeleteButton>
                                            )}
                                        </SocialInputContainer>
                                    </SocialField>
                                </SocialGrid>
                            </Section>

                            <Section>
                                <SectionTitle>Personal strengths</SectionTitle>
                                <StrengthsContainer>
                                    <StrengthTags>
                                        {strengths.map((strength, index) => {
                                            const colors = getStrengthColors(strength);
                                            return (
                                                <StrengthTag
                                                    key={index}
                                                    bgColor={colors.bg}
                                                    textColor={colors.text}
                                                >
                                                    {strength}
                                                    <RemoveButton onClick={() => removeStrength(strength)}>×</RemoveButton>
                                                </StrengthTag>
                                            );
                                        })}
                                    </StrengthTags>
                                    <StrengthInput
                                        value={strengthInput}
                                        onChange={(e) => setStrengthInput(e.target.value)}
                                        onKeyPress={handleStrengthInputKeyPress}
                                        placeholder="Tell us your strengths..."
                                    />
                                    <PredefinedStrengthsSection>
                                        <PredefinedStrengthsTitle>Quick Add:</PredefinedStrengthsTitle>
                                        <PredefinedStrengthsTags>
                                            {predefinedStrengths.map((strength, index) => (
                                                <PredefinedStrengthTag
                                                    key={index}
                                                    onClick={() => addPredefinedStrength(strength)}
                                                    disabled={strengths.includes(strength)}
                                                >
                                                    {strength}
                                                </PredefinedStrengthTag>
                                            ))}
                                        </PredefinedStrengthsTags>
                                    </PredefinedStrengthsSection>
                                </StrengthsContainer>
                            </Section>

                            {saveMessage && (
                                <SaveMessage success={saveMessage.includes('successfully')}>
                                    {saveMessage}
                                </SaveMessage>
                            )}

                            <ButtonContainer>
                                <CancelButton onClick={handleCancel} disabled={saving || !hasChanges}>
                                    Cancel
                                </CancelButton>
                                <SaveChangesButton
                                    onClick={handleSaveChanges}
                                    disabled={!hasChanges || saving}
                                    $hasChanges={hasChanges}
                                >
                                    {saving ? 'Saving...' : 'Save Changes'}
                                </SaveChangesButton>
                            </ButtonContainer>
                    </Card>
                </Container>
            </PageWrapper>
        </>
    );
};

const PageWrapper = styled.div`
    background-color: #f8fafc;
    display: flex;
    flex-direction: column;
    padding: 15px 20px;
`;

const Container = styled.div`
    margin-left: auto;
    margin-right: auto;
    padding-left: 1rem;
    padding-right: 1rem;
    padding-top: 3rem;
    padding-bottom: 3rem;
    max-width: 56rem;
`;

const BackButtonWrapper = styled.div`
    position: relative;
    display: flex;
    justify-content: flex-start;
    margin-bottom: 1rem;
`;

const BackButton = styled.button`
    display: inline-flex;
    align-items: center;
    background-color: #fafafc;
    color: #333333;
    border: 3px solid rgba(0, 0, 0, 0.04);
    padding: 8px 14px;
    border-radius: 11px;
    font-family: "SF Pro Text", system-ui, -apple-system, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.29;
    letter-spacing: -0.224px;
    cursor: pointer;
    transition: transform 0.1s ease-in-out;

    &:active {
        transform: scale(0.95);
    }
`;

const Header = styled.div`
    text-align: center;
    margin-bottom: 1.5rem;
`;

const Title = styled.h1`
    font-size: 2.25rem;
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 1rem;
`;

const Subtitle = styled.p`
    font-size: 1.125rem;
    color: #4b5563;
`;

const Card = styled.div`
    background: #fff;
    padding: 30px;
    border-radius: 12px;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const Section = styled.div`
    background: white;
    border-radius: 12px;
    padding: 20px;
    margin-bottom: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
    font-size: 18px;
    font-weight: 600;
    color: #333;
    margin: 0 0 8px 0;
    font-family: 'Inter', sans-serif;
`;

const PersonalInfoGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
`;

const InfoField = styled.div`
    display: flex;
    flex-direction: column;
`;

const BioFieldContainer = styled.div`
    grid-column: 1 / -1;
    display: flex;
    flex-direction: column;
`;

const Label = styled.label`
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 6px;
    font-family: 'Inter', sans-serif;
`;

const Input = styled.input`
    padding: 10px 14px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    font-family: 'Inter', sans-serif;
    background: #fff;
    transition: border-color 0.2s, box-shadow 0.2s;
    width: 100%;
    box-sizing: border-box;

    &:focus {
        outline: none;
        border-color: #9333ea;
        box-shadow: 0 0 0 2px rgba(147, 51, 234, 0.15);
    }

    &::placeholder {
        color: #9ca3af;
    }
`;

const ReadOnlyInput = styled(Input)`
    background-color: #f9fafb;
    color: #374151;
    cursor: not-allowed;
    border-color: #e5e7eb;

    &:focus {
        border-color: #e5e7eb;
        box-shadow: none;
    }
`;

const BioTextArea = styled.textarea`
    padding: 10px 14px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    font-family: 'Inter', sans-serif;
    resize: vertical;
    min-height: 80px;
    background: #fff;
    transition: border-color 0.2s, box-shadow 0.2s;

    &:focus {
        outline: none;
        border-color: #9333ea;
        box-shadow: 0 0 0 2px rgba(147, 51, 234, 0.15);
    }

    &::placeholder {
        color: #9ca3af;
    }
`;

const SocialGrid = styled.div`
    margin-top: 16px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
`;

const SocialField = styled.div`
    margin-top: 12px;
    display: flex;
    flex-direction: column;
`;

const SocialHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
    width: 100%;
`;

const SocialIconImage = styled.img`
    width: 28px;
    height: 28px;
    border-radius: 8px;
    object-fit: contain;
    flex-shrink: 0;
`;

const SocialLabel = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: #1f2937;
    font-family: 'Inter', sans-serif;
`;

const ManageLink = styled.button`
    margin-left: auto;
    padding: 0;
    font-size: 13px;
    font-weight: 600;
    color: #9333ea;
    background: none;
    border: none;
    cursor: pointer;
    text-decoration: none;
    transition: color 0.2s;

    &:hover {
        color: #7e22ce;
        scale: 1.01;
        transition: scale 0.2s ease-in-out;
    }
`;

const ReadOnlyInputWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;

    input {
        padding-right: 40px;
    }
`;

const LockIcon = styled.span`
    position: absolute;
    right: 14px;
    color: #9ca3af;
    font-size: 13px;
    pointer-events: none;
`;

const StrengthsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
`;

const StrengthTags = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const StrengthTag = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    font-family: 'Inter', sans-serif;
    position: relative;
    background-color: ${props => props.bgColor || '#667eea'};
    color: ${props => props.textColor || 'white'};
`;

const RemoveButton = styled.button`
    background: none;
    border: none;
    color: inherit;
    font-size: 14px;
    cursor: pointer;
    padding: 0;
    margin-left: 4px;
    opacity: 0.7;
    
    &:hover {
        opacity: 1;
    }
`;

const StrengthInput = styled.input`
    padding: 10px 14px;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    font-size: 14px;
    font-family: 'Inter', sans-serif;
    background: #fff;
    transition: border-color 0.2s, box-shadow 0.2s;

    &:focus {
        outline: none;
        border-color: #9333ea;
        box-shadow: 0 0 0 2px rgba(147, 51, 234, 0.15);
    }

    &::placeholder {
        color: #9ca3af;
    }
`;

const ButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    gap: 16px;
    margin-top: 20px;
    padding-top: 16px;
`;

const CancelButton = styled.button`
    padding: 12px 24px;
    border: 1px solid #ddd;
    border-radius: 6px;
    background: white;
    color: #666;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    font-family: 'Inter', sans-serif;
    transition: all 0.2s;

    &:hover:not(:disabled) {
        background-color: #f5f5f5;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const SaveChangesButton = styled.button`
    padding: 12px 24px;
    border: none;
    border-radius: 8px;
    background-color: ${props => props.$hasChanges ? '#9333ea' : '#ccc'};
    color: white;
    font-size: 14px;
    font-weight: 600;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    font-family: 'Inter', sans-serif;
    transition: background-color 0.2s;

    &:hover:not(:disabled) {
        background-color: ${props => props.hasChanges ? '#7e22ce' : '#ccc'};
    }

    &:disabled {
        opacity: 0.7;
    }
`;

const SaveMessage = styled.div`
    text-align: center;
    font-size: 14px;
    color: ${props => props.success ? '#28a745' : '#dc3545'};
    font-family: 'Inter', sans-serif;
    background: white;
    padding: 12px;
    border-radius: 6px;
    margin-bottom: 16px;
`;

const LoadingMessage = styled.div`
    text-align: center;
    font-size: 16px;
    color: #666;
    padding: 40px;
    font-family: 'Inter', sans-serif;
`;

const PhoneInputContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
`;

const PhoneDeleteButton = styled.button`
    position: absolute;
    right: 12px;
    background: none;
    border: none;
    color: #9ca3af;
    font-size: 16px;
    cursor: pointer;
    padding: 2px;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
        background-color: #f3f4f6;
        color: #6b7280;
    }
`;

const SocialInputContainer = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    width: 100%;
`;

const SocialDeleteButton = styled.button`
    position: absolute;
    right: 12px;
    background: none;
    border: none;
    color: #9ca3af;
    font-size: 16px;
    cursor: pointer;
    padding: 2px;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    
    &:hover {
        background-color: #f3f4f6;
        color: #6b7280;
    }
`;

const PredefinedStrengthsSection = styled.div`
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px solid #e0e0e0;
`;

const PredefinedStrengthsTitle = styled.h4`
    font-size: 14px;
    font-weight: 500;
    color: #333;
    margin: 0 0 12px 0;
    font-family: 'Inter', sans-serif;
`;

const PredefinedStrengthsTags = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
`;

const PredefinedStrengthTag = styled.button`
    display: inline-flex;
    align-items: center;
    padding: 6px 12px;
    border: 1px solid #e0e0e0;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;
    font-family: 'Inter', sans-serif;
    background-color: ${props => props.disabled ? '#f5f5f5' : 'white'};
    color: ${props => props.disabled ? '#999' : '#333'};
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.2s;
    
    &:hover:not(:disabled) {
        background-color: #667eea;
        color: white;
        border-color: #667eea;
    }
    
    &:disabled {
        opacity: 0.6;
    }
`;

export default Settings;
